import React, { useState, useEffect } from 'react';
import { FiSearch, FiPlus, FiMinus, FiShoppingCart, FiEdit2, FiArrowLeft, FiTrash2, FiUser, FiX } from 'react-icons/fi';

function MobileCreateOrderContent({
  menuSearchTerm, setMenuSearchTerm,
  selectedMenuCategory, setSelectedMenuCategory,
  categories, getDisplayedMenuItems,
  configuringItem, setConfiguringItem,
  itemOptions, setItemOptions,
  toppings, toggleTopping, getItemOptions,
  getItemConfigPrice, formatCurrency,
  tables,
  createOrderForm, setCreateOrderForm,
  createOrderCustomerSearch, setCreateOrderCustomerSearch, searchCreateOrderCustomers,
  showCustomerSearchDropdown, setShowCustomerSearchDropdown,
  getFilteredCustomersForOrder, selectCustomerForOrder, clearSelectedCustomer,
  removeCreateOrderItem, updateCreateOrderItemQty,
  getCreateOrderTotal, getCreateOrderDiscount, getCreateOrderFinalTotal, getCreateOrderExtraFee, submitCreateOrder,
  selectedCustomerDiscount, setSelectedCustomerDiscount,
  activeDiscounts,
  expenseGroups, expenseRecipients,
  menuListRef, handleMenuScroll,
  displayedItemsCount, getFilteredMenuItems,
  onClose
}) {
  const [activeView, setActiveView] = useState('menu');
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  
  // Local state for search input to prevent parent re-renders on every keystroke
  const [localSearchTerm, setLocalSearchTerm] = useState(menuSearchTerm);
  
  // Sync local search to parent with debounce
  useEffect(() => {
    if (localSearchTerm === menuSearchTerm) return;
    
    const timer = setTimeout(() => {
      setMenuSearchTerm(localSearchTerm);
    }, 200);
    return () => clearTimeout(timer);
  }, [localSearchTerm, menuSearchTerm, setMenuSearchTerm]);
  
  // Manual discount state
  const [manualDiscountType, setManualDiscountType] = useState('fixed'); // 'fixed' or 'percent'
  const [manualDiscountValue, setManualDiscountValue] = useState('');
  
  const cartItemCount = createOrderForm.items.reduce((sum, i) => sum + i.quantity, 0);
  
  // Calculate manual discount amount
  const getManualDiscountAmount = () => {
    const value = parseFloat(manualDiscountValue) || 0;
    if (value <= 0) return 0;
    
    const subtotal = getCreateOrderTotal();
    if (manualDiscountType === 'percent') {
      return Math.round(subtotal * value / 100);
    }
    return value;
  };
  
  // Sync manual discount to createOrderForm whenever it changes
  // Note: Only depend on manualDiscountType and manualDiscountValue, NOT on functions
  // to avoid infinite re-render loop
  useEffect(() => {
    if (!manualDiscountValue) {
      // Only update if there was a previous value
      setCreateOrderForm(prev => {
        if (prev.manualDiscount) {
          return { ...prev, manualDiscount: null };
        }
        return prev;
      });
      return;
    }
    
    const value = parseFloat(manualDiscountValue) || 0;
    if (value <= 0) return;
    
    // Calculate amount based on type
    // We can't use getCreateOrderTotal here to avoid dependency issues
    // So we calculate inline
    const itemsTotal = createOrderForm.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const amount = manualDiscountType === 'percent' 
      ? Math.round(itemsTotal * value / 100)
      : value;
    
    setCreateOrderForm(prev => ({
      ...prev,
      manualDiscount: {
        type: manualDiscountType,
        value: value,
        amount: amount
      }
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualDiscountType, manualDiscountValue]);
  
  // Calculate final total with manual discount
  const getFinalTotalWithManualDiscount = () => {
    const baseTotal = getCreateOrderFinalTotal(); // Already includes promotion discount and extra fee
    const manualDiscount = getManualDiscountAmount();
    return Math.max(0, baseTotal - manualDiscount);
  };
  
  const cartTotal = getFinalTotalWithManualDiscount();

  // Compact currency
  const compactPrice = (amount) => {
    if (amount >= 1000) return Math.round(amount / 1000) + 'k';
    return amount.toLocaleString('vi-VN');
  };

  // Get cart quantity for menu item
  const getCartQty = (menuItem) => {
    return createOrderForm.items
      .filter(i => i.menuItemId === menuItem._id)
      .reduce((sum, i) => sum + i.quantity, 0);
  };

  // Quick add with defaults
  const quickAdd = (item) => {
    const opts = getItemOptions(item);
    const defaultOpt = item.customOptions?.[0] || null;
    
    setCreateOrderForm(prev => ({
      ...prev,
      items: [...prev.items, {
        menuItemId: item._id,
        name: item.name,
        basePrice: item.price,
        quantity: 1,
        selectedOption: defaultOpt,
        temperature: opts.showIceOptions ? 'Mặc định' : null,
        sweetness: opts.showSweetnessOptions ? 'Mặc định' : null,
        toppings: [],
        note: '',
        price: item.price + (defaultOpt?.price || 0)
      }]
    }));
  };

  // Update quantity
  const updateQty = (menuItem, delta) => {
    const idx = createOrderForm.items.findIndex(i => i.menuItemId === menuItem._id);
    if (idx === -1 && delta > 0) {
      quickAdd(menuItem);
    } else if (idx !== -1) {
      const newItems = [...createOrderForm.items];
      const newQty = newItems[idx].quantity + delta;
      if (newQty <= 0) {
        setCreateOrderForm(prev => ({ ...prev, items: newItems.filter(i => i.menuItemId !== menuItem._id) }));
      } else {
        newItems[idx].quantity = newQty;
        setCreateOrderForm(prev => ({ ...prev, items: newItems }));
      }
    }
  };

  // Set exact quantity
  const setQty = (menuItem, qty) => {
    if (qty <= 0) {
      setCreateOrderForm(prev => ({ ...prev, items: prev.items.filter(i => i.menuItemId !== menuItem._id) }));
    } else {
      const idx = createOrderForm.items.findIndex(i => i.menuItemId === menuItem._id);
      if (idx === -1) {
        const opts = getItemOptions(menuItem);
        const defaultOpt = menuItem.customOptions?.[0] || null;
        setCreateOrderForm(prev => ({
          ...prev,
          items: [...prev.items, {
            menuItemId: menuItem._id,
            name: menuItem.name,
            basePrice: menuItem.price,
            quantity: qty,
            selectedOption: defaultOpt,
            temperature: opts.showIceOptions ? 'Mặc định' : null,
            sweetness: opts.showSweetnessOptions ? 'Mặc định' : null,
            toppings: [],
            note: '',
            price: menuItem.price + (defaultOpt?.price || 0)
          }]
        }));
      } else {
        const newItems = [...createOrderForm.items];
        newItems[idx].quantity = qty;
        setCreateOrderForm(prev => ({ ...prev, items: newItems }));
      }
    }
  };

  // Open edit
  const openEdit = (menuItem) => {
    const item = createOrderForm.items.find(i => i.menuItemId === menuItem._id);
    if (item) {
      setEditingItemIndex(createOrderForm.items.indexOf(item));
      setItemOptions({
        selectedOption: item.selectedOption,
        temperature: item.temperature || 'Mặc định',
        sweetness: item.sweetness || 'Mặc định',
        selectedToppings: item.toppings || [],
        quantity: item.quantity,
        note: item.note || ''
      });
      setConfiguringItem(menuItem);
      setActiveView('config');
    }
  };

  // Save edit
  const saveEdit = () => {
    if (editingItemIndex !== null && configuringItem) {
      const newItems = [...createOrderForm.items];
      const optPrice = itemOptions.selectedOption?.price || 0;
      const topPrice = itemOptions.selectedToppings.reduce((s, t) => s + t.price, 0);
      
      newItems[editingItemIndex] = {
        ...newItems[editingItemIndex],
        selectedOption: itemOptions.selectedOption,
        temperature: itemOptions.temperature,
        sweetness: itemOptions.sweetness,
        toppings: itemOptions.selectedToppings,
        quantity: itemOptions.quantity,
        note: itemOptions.note,
        price: configuringItem.price + optPrice + topPrice
      };
      
      setCreateOrderForm(prev => ({ ...prev, items: newItems }));
      setConfiguringItem(null);
      setEditingItemIndex(null);
      setActiveView('menu');
    }
  };

  // Price display
  const priceDisplay = (item) => {
    if (item.customOptions?.length > 0) {
      const prices = item.customOptions.map(o => item.price + o.price);
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      if (min !== max) return `${compactPrice(min)}-${compactPrice(max)}`;
    }
    return compactPrice(item.price);
  };

  // Product Card
  const ProductCard = ({ item }) => {
    const qty = getCartQty(item);
    const inCart = qty > 0;

    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden relative">
        {/* Edit button - top right */}
        {inCart && (
          <button 
            onClick={(e) => { e.stopPropagation(); openEdit(item); }} 
            className="absolute top-1 right-1 z-10 w-6 h-6 bg-blue-500 text-white rounded-full shadow flex items-center justify-center"
          >
            <FiEdit2 size={12} />
          </button>
        )}
        
        <div className="relative aspect-square bg-gray-100">
          {item.image ? (
            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <FiShoppingCart size={20} />
            </div>
          )}
          
          {/* Quantity controls or Add button */}
          {inCart ? (
            <div className="absolute bottom-1 left-1 right-1 flex items-center justify-center">
              <div className="flex items-center bg-white rounded-lg shadow border">
                <button onClick={(e) => { e.stopPropagation(); updateQty(item, -1); }} className="w-7 h-7 flex items-center justify-center text-gray-600">
                  <FiMinus size={14} />
                </button>
                <input
                  type="number"
                  value={qty}
                  onChange={(e) => { e.stopPropagation(); setQty(item, parseInt(e.target.value) || 0); }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-8 h-7 text-center text-sm font-bold border-x focus:outline-none"
                />
                <button onClick={(e) => { e.stopPropagation(); updateQty(item, 1); }} className="w-7 h-7 flex items-center justify-center text-green-600">
                  <FiPlus size={14} />
                </button>
              </div>
            </div>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); quickAdd(item); }} className="absolute bottom-1 right-1 w-8 h-8 bg-green-500 text-white rounded-full shadow flex items-center justify-center">
              <FiPlus size={16} />
            </button>
          )}
        </div>
        <div className="p-1.5">
          <h3 className="font-medium text-sm text-gray-800 line-clamp-2 leading-tight">{item.name}</h3>
          <p className="text-green-600 font-bold text-sm">{priceDisplay(item)}</p>
        </div>
      </div>
    );
  };

  // MENU VIEW
  const MenuView = () => (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Search - uses local state to prevent parent re-renders */}
      <div className="p-2 bg-white flex-shrink-0">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Tìm theo tên"
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-full text-sm focus:outline-none"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white border-b overflow-x-auto flex-shrink-0">
        <div className="flex px-2 py-1.5 gap-2">
          <button
            onClick={() => setSelectedMenuCategory('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
              selectedMenuCategory === 'all' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Tất cả
          </button>
          {categories.map(cat => (
            <button
              key={cat._id}
              onClick={() => setSelectedMenuCategory(cat.name)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
                selectedMenuCategory === cat.name ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products - 3 columns */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden p-2 bg-gray-50"
        ref={menuListRef}
        onScroll={handleMenuScroll}
        style={{ 
          touchAction: 'pan-y', 
          WebkitOverflowScrolling: 'touch',
          paddingBottom: cartItemCount === 0 ? 'max(0.5rem, env(safe-area-inset-bottom))' : undefined
        }}
      >
        <div className="grid grid-cols-3 gap-2" style={{ minWidth: 0 }}>
          {getDisplayedMenuItems().map(item => (
            <ProductCard key={item._id} item={item} />
          ))}
        </div>
        {displayedItemsCount < getFilteredMenuItems().length && (
          <div className="text-center py-2 text-gray-400 text-xs">Cuộn để xem thêm...</div>
        )}
        {getDisplayedMenuItems().length === 0 && (
          <div className="text-center py-4 text-gray-400 text-sm">Không tìm thấy</div>
        )}
      </div>

      {/* Bottom Bar - with safe area padding for iPhone */}
      {cartItemCount > 0 && (
        <div 
          className="bg-white border-t p-2.5 flex items-center gap-3 flex-shrink-0"
          style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
        >
          <button onClick={() => setActiveView('cart')} className="relative p-2 bg-green-100 rounded-xl">
            <FiShoppingCart className="text-green-600" size={22} />
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
              {cartItemCount}
            </span>
          </button>
          <span className="flex-1 text-base font-bold">{formatCurrency(cartTotal)}</span>
          <button onClick={() => setActiveView('checkout')} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-bold">
            Tiếp tục →
          </button>
        </div>
      )}
    </div>
  );

  // CART VIEW
  const CartView = () => (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="bg-white p-3 border-b flex items-center gap-2 flex-shrink-0">
        <button onClick={() => setActiveView('menu')} className="p-0.5"><FiArrowLeft size={22} /></button>
        <h2 className="text-lg font-bold flex-1">Giỏ hàng ({cartItemCount})</h2>
        {cartItemCount > 0 && (
          <button onClick={() => setCreateOrderForm(prev => ({ ...prev, items: [] }))} className="text-red-500 text-sm">Xóa tất cả</button>
        )}
      </div>

      <div 
        className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50" 
        style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch', paddingBottom: cartItemCount === 0 ? 'max(0.75rem, env(safe-area-inset-bottom))' : undefined }}
      >
        {createOrderForm.items.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-base">Giỏ hàng trống</div>
        ) : (
          createOrderForm.items.map((item, idx) => (
            <div key={idx} className="bg-white rounded-lg p-3 shadow-sm flex gap-3">
              <div className="flex-1">
                <h3 className="font-medium text-sm">{item.name}</h3>
                <div className="text-xs text-gray-500 mt-0.5">
                  {item.selectedOption && <span>Size: {item.selectedOption.name} </span>}
                  {item.temperature && item.temperature !== 'Mặc định' && <span>• {item.temperature} </span>}
                  {item.toppings?.length > 0 && <span>• +{item.toppings.length} topping</span>}
                </div>
                <div className="text-green-600 font-bold text-sm mt-1">{formatCurrency(item.price * item.quantity)}</div>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => removeCreateOrderItem(idx)} className="text-red-400"><FiTrash2 size={18} /></button>
                <div className="flex items-center bg-gray-100 rounded-lg">
                  <button onClick={() => updateCreateOrderItemQty(idx, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center"><FiMinus size={14} /></button>
                  <span className="w-8 text-center font-bold text-sm">{item.quantity}</span>
                  <button onClick={() => updateCreateOrderItemQty(idx, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-green-600"><FiPlus size={14} /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {cartItemCount > 0 && (
        <div 
          className="bg-white border-t p-3 flex-shrink-0"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Tổng cộng</span>
            <span className="text-lg font-bold text-green-600">{formatCurrency(cartTotal)}</span>
          </div>
          <button onClick={() => setActiveView('checkout')} className="w-full py-3 bg-green-600 text-white rounded-lg text-base font-bold">
            Tiếp tục thanh toán
          </button>
        </div>
      )}
    </div>
  );

  // CONFIG VIEW
  const ConfigView = () => {
    if (!configuringItem) return null;
    const opts = getItemOptions(configuringItem);
    
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white p-3 border-b flex items-center gap-2 flex-shrink-0">
          <button onClick={() => { setActiveView('menu'); setConfiguringItem(null); setEditingItemIndex(null); }} className="p-0.5">
            <FiArrowLeft size={22} />
          </button>
          <h2 className="text-lg font-bold flex-1 truncate">{configuringItem.name}</h2>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
          {/* Size */}
          {configuringItem.customOptions?.length > 0 && (
            <div className="bg-white p-3 rounded-lg">
              <label className="block text-sm font-semibold mb-2 text-gray-600">Size</label>
              <div className="flex flex-wrap gap-2">
                {configuringItem.customOptions.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => setItemOptions({ ...itemOptions, selectedOption: opt })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      itemOptions.selectedOption?.name === opt.name ? 'bg-green-600 text-white' : 'bg-gray-100'
                    }`}
                  >
                    {opt.name} {opt.price > 0 && `+${compactPrice(opt.price)}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Ice */}
          {opts.showIceOptions && (
            <div className="bg-white p-3 rounded-lg">
              <label className="block text-sm font-semibold mb-2 text-gray-600">Đá</label>
              <div className="flex flex-wrap gap-2">
                {['Mặc định', 'Ít đá', 'Không đá', ...(configuringItem.hasHotOption ? ['Nóng'] : [])].map(t => (
                  <button
                    key={t}
                    onClick={() => setItemOptions({ ...itemOptions, temperature: t })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      itemOptions.temperature === t ? 'bg-blue-600 text-white' : 'bg-gray-100'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sweetness */}
          {opts.showSweetnessOptions && (
            <div className="bg-white p-3 rounded-lg">
              <label className="block text-sm font-semibold mb-2 text-gray-600">Đường</label>
              <div className="flex flex-wrap gap-2">
                {['Mặc định', 'Ít ngọt', 'Không đường', 'Thêm ngọt'].map(s => (
                  <button
                    key={s}
                    onClick={() => setItemOptions({ ...itemOptions, sweetness: s })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      itemOptions.sweetness === s ? 'bg-orange-500 text-white' : 'bg-gray-100'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Toppings */}
          {toppings.length > 0 && opts.showToppingOptions && (
            <div className="bg-white p-3 rounded-lg">
              <label className="block text-sm font-semibold mb-2 text-gray-600">Topping</label>
              <div className="space-y-2">
                {toppings.filter(t => t.isAvailable !== false).map(top => (
                  <label key={top._id} className={`flex items-center p-2.5 rounded-lg border cursor-pointer text-sm ${
                    itemOptions.selectedToppings.some(t => t._id === top._id) ? 'bg-pink-50 border-pink-400' : 'border-gray-200'
                  }`}>
                    <input
                      type="checkbox"
                      checked={itemOptions.selectedToppings.some(t => t._id === top._id)}
                      onChange={() => toggleTopping(top)}
                      className="mr-3 w-4 h-4"
                    />
                    <span className="flex-1">{top.name}</span>
                    <span className="text-pink-600 font-bold">+{compactPrice(top.price)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="bg-white p-3 rounded-lg">
            <label className="block text-sm font-semibold mb-2 text-gray-600">Số lượng</label>
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => setItemOptions({ ...itemOptions, quantity: Math.max(1, itemOptions.quantity - 1) })} className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <FiMinus size={18} />
              </button>
              <input
                type="number"
                min="1"
                value={itemOptions.quantity}
                onChange={(e) => setItemOptions({ ...itemOptions, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                className="w-16 h-10 text-center text-xl font-bold border rounded-lg focus:outline-none"
              />
              <button onClick={() => setItemOptions({ ...itemOptions, quantity: itemOptions.quantity + 1 })} className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center">
                <FiPlus size={18} />
              </button>
            </div>
          </div>

          {/* Note */}
          <div className="bg-white p-3 rounded-lg">
            <label className="block text-sm font-semibold mb-2 text-gray-600">Ghi chú</label>
            <textarea
              value={itemOptions.note || ''}
              onChange={(e) => setItemOptions({ ...itemOptions, note: e.target.value })}
              placeholder="Ghi chú..."
              className="w-full border rounded-lg p-2.5 text-sm resize-none"
              rows="2"
            />
          </div>
        </div>

        <div 
          className="bg-white border-t p-3 flex-shrink-0"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <button onClick={saveEdit} className="w-full py-3 bg-green-600 text-white rounded-lg text-base font-bold">
            Lưu - {formatCurrency(getItemConfigPrice())}
          </button>
        </div>
      </div>
    );
  };

  // CHECKOUT VIEW
  const CheckoutView = () => (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="bg-white p-3 border-b flex items-center gap-2 flex-shrink-0">
        <button onClick={() => setActiveView('menu')} className="p-0.5"><FiArrowLeft size={22} /></button>
        <h2 className="text-lg font-bold flex-1">Xác nhận đơn</h2>
      </div>

      <div 
        className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50" 
        style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        {/* Table */}
        <div className="bg-white p-3 rounded-lg">
          <label className="block text-sm font-semibold mb-2 text-gray-600">Bàn</label>
          <select
            value={createOrderForm.tableId}
            onChange={(e) => setCreateOrderForm({ ...createOrderForm, tableId: e.target.value })}
            className="w-full border rounded-lg px-3 py-2.5 text-sm"
          >
            <option value="">-- Không chọn --</option>
            {tables.map(t => <option key={t._id} value={t._id}>{t.name || `Bàn ${t.tableNumber}`}</option>)}
          </select>
        </div>

        {/* Customer */}
        <div className="bg-white p-3 rounded-lg">
          <label className="block text-sm font-semibold mb-2 text-gray-600">Khách hàng</label>
          {createOrderForm.customerId ? (
            <div className="flex items-center justify-between p-2.5 bg-green-50 rounded-lg border border-green-200 text-sm">
              <span className="flex items-center gap-2"><FiUser size={16} /> {createOrderForm.customerName}</span>
              <button onClick={clearSelectedCustomer} className="text-red-500"><FiX size={18} /></button>
            </div>
          ) : (
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Tìm khách..."
                value={createOrderCustomerSearch}
                onChange={(e) => { searchCreateOrderCustomers(e.target.value); setShowCustomerSearchDropdown(true); }}
                onFocus={() => setShowCustomerSearchDropdown(true)}
                className="w-full pl-10 pr-3 py-2.5 border rounded-lg text-sm"
              />
              {showCustomerSearchDropdown && createOrderCustomerSearch && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {getFilteredCustomersForOrder().slice(0, 5).map(c => (
                    <button key={c._id} onClick={() => selectCustomerForOrder(c)} className="w-full px-3 py-2.5 text-left hover:bg-gray-50 text-sm border-b">
                      {c.name} - {c.phone}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order Items */}
        <div className="bg-white p-3 rounded-lg">
          <label className="block text-sm font-semibold mb-2 text-gray-600">Đơn hàng</label>
          <div className="space-y-1 text-sm">
            {createOrderForm.items.map((item, i) => (
              <div key={i} className="flex justify-between py-1.5 border-b border-dashed">
                <span>{item.quantity}x {item.name}</span>
                <span className="font-medium">{formatCurrency(item.price * item.quantity)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Discount */}
        {activeDiscounts.length > 0 && (
          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <label className="block text-sm font-semibold mb-2 text-yellow-800">🎁 Khuyến mãi</label>
            <div className="space-y-2">
              {activeDiscounts.map(d => (
                <label key={d._id} className={`flex items-center p-2.5 rounded-lg text-sm cursor-pointer ${
                  selectedCustomerDiscount?._id === d._id ? 'bg-green-100 border border-green-400' : 'bg-white'
                }`}>
                  <input
                    type="radio"
                    checked={selectedCustomerDiscount?._id === d._id}
                    onChange={() => setSelectedCustomerDiscount(d)}
                    className="mr-3 w-4 h-4"
                  />
                  <span className="flex-1">{d.name}</span>
                  <span className="text-green-600 font-bold">-{d.percentage}%</span>
                </label>
              ))}
              {selectedCustomerDiscount && (
                <button onClick={() => setSelectedCustomerDiscount(null)} className="text-sm text-red-600">✕ Bỏ chọn</button>
              )}
            </div>
          </div>
        )}

        {/* Manual Discount */}
        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-green-800">🏷️ Giảm giá</label>
            {manualDiscountValue && parseFloat(manualDiscountValue) > 0 && (
              <button 
                onClick={() => setManualDiscountValue('')}
                className="text-red-500 text-xs"
              >
                ✕ Xóa
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Toggle VND / % */}
            <div className="flex bg-white rounded-lg border overflow-hidden">
              <button
                onClick={() => setManualDiscountType('fixed')}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  manualDiscountType === 'fixed' 
                    ? 'bg-green-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                VNĐ
              </button>
              <button
                onClick={() => setManualDiscountType('percent')}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  manualDiscountType === 'percent' 
                    ? 'bg-green-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                %
              </button>
            </div>
            
            {/* Input */}
            <div className="flex-1 relative">
              <input
                type="number"
                placeholder={manualDiscountType === 'fixed' ? 'Số tiền giảm' : 'Phần trăm giảm'}
                value={manualDiscountValue}
                onChange={(e) => {
                  let val = e.target.value;
                  // Limit percent to 100
                  if (manualDiscountType === 'percent' && parseFloat(val) > 100) {
                    val = '100';
                  }
                  setManualDiscountValue(val);
                }}
                className="w-full border rounded-lg px-3 py-2 text-sm pr-12"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                {manualDiscountType === 'fixed' ? 'đ' : '%'}
              </span>
            </div>
          </div>
          
          {/* Show calculated discount */}
          {manualDiscountValue && parseFloat(manualDiscountValue) > 0 && (
            <div className="mt-2 text-sm text-green-700 font-medium">
              Giảm: -{formatCurrency(getManualDiscountAmount())}
            </div>
          )}
        </div>

        {/* Extra Fee */}
        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
          <label className="block text-sm font-semibold mb-2 text-orange-800">💰 Phí thêm</label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              placeholder="Số tiền"
              value={createOrderForm.extraFee?.amount || ''}
              onChange={(e) => setCreateOrderForm(prev => ({
                ...prev, extraFee: { ...prev.extraFee, amount: parseInt(e.target.value) || 0 }
              }))}
              className="border rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Nội dung"
              value={createOrderForm.extraFee?.description || ''}
              onChange={(e) => setCreateOrderForm(prev => ({
                ...prev, extraFee: { ...prev.extraFee, description: e.target.value }
              }))}
              className="border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Note */}
        <div className="bg-white p-3 rounded-lg">
          <label className="block text-sm font-semibold mb-2 text-gray-600">Ghi chú</label>
          <textarea
            placeholder="Ghi chú đơn hàng..."
            value={createOrderForm.customerNote}
            onChange={(e) => setCreateOrderForm({ ...createOrderForm, customerNote: e.target.value })}
            className="w-full border rounded-lg p-3 text-sm resize-none"
            rows="2"
          />
        </div>

        {/* Scheduled Delivery Time */}
        <div className="bg-red-50 p-3 rounded-lg border border-red-200">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-red-700">⏰ Hẹn giờ giao hàng</label>
            {(createOrderForm.deliveryDate || createOrderForm.deliveryTime) && (
              <button
                type="button"
                onClick={() => setCreateOrderForm({ ...createOrderForm, deliveryDate: '', deliveryTime: '' })}
                className="text-xs text-red-500"
              >
                ✕ Bỏ hẹn
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={createOrderForm.deliveryDate || ''}
              onChange={(e) => setCreateOrderForm({ ...createOrderForm, deliveryDate: e.target.value })}
              className="border rounded-lg px-2 py-2.5 text-sm"
            />
            <input
              type="time"
              value={createOrderForm.deliveryTime || ''}
              onChange={(e) => setCreateOrderForm({ ...createOrderForm, deliveryTime: e.target.value })}
              className="border rounded-lg px-2 py-2.5 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="bg-white border-t p-3 flex-shrink-0">
        <div className="space-y-1 text-sm mb-2">
          <div className="flex justify-between"><span>Tạm tính:</span><span>{formatCurrency(getCreateOrderTotal())}</span></div>
          {selectedCustomerDiscount && (
            <div className="flex justify-between text-green-600"><span>KM ({selectedCustomerDiscount.name}):</span><span>-{formatCurrency(getCreateOrderDiscount())}</span></div>
          )}
          {getManualDiscountAmount() > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Giảm giá ({manualDiscountType === 'percent' ? `${manualDiscountValue}%` : 'cố định'}):</span>
              <span>-{formatCurrency(getManualDiscountAmount())}</span>
            </div>
          )}
          {getCreateOrderExtraFee && getCreateOrderExtraFee() > 0 && (
            <div className="flex justify-between text-orange-600"><span>Phí thêm:</span><span>+{formatCurrency(getCreateOrderExtraFee())}</span></div>
          )}
        </div>
        <div className="flex justify-between items-center mb-3 pt-2 border-t">
          <span className="font-bold text-base">Tổng:</span>
          <span className="text-xl font-bold text-green-600">{formatCurrency(cartTotal)}</span>
        </div>
        <button
          onClick={submitCreateOrder}
          disabled={createOrderForm.items.length === 0}
          className="w-full py-3 bg-green-600 text-white rounded-lg text-base font-bold disabled:opacity-50"
        >
          Tạo đơn hàng
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-green-600 text-white px-3 py-2.5 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          {onClose && <button onClick={onClose}><FiArrowLeft size={22} /></button>}
          <h1 className="text-lg font-bold">Bán hàng</h1>
        </div>
        <button onClick={() => setActiveView('cart')} className="relative flex items-center gap-1">
          <FiShoppingCart size={22} />
          {cartItemCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-[10px] rounded-full flex items-center justify-center font-bold">
              {cartItemCount}
            </span>
          )}
        </button>
      </div>

      {/* Content - Render views as JSX, not as components to avoid remount */}
      {activeView === 'menu' && MenuView()}
      {activeView === 'cart' && CartView()}
      {activeView === 'config' && ConfigView()}
      {activeView === 'checkout' && CheckoutView()}
    </div>
  );
}

export default MobileCreateOrderContent;
